/**
 * Asking for a permission you do not hold (WALLIYO-0131).
 *
 * It sits on your own profile, beside what you already hold, because that is
 * the screen that tells you where your ceiling is — and the only useful thing
 * to do with that knowledge is ask.
 *
 * It opens a case for whoever can grant it. Being refused is a normal answer,
 * and asking needs no permission of its own: a gate here would mean needing
 * permission to ask for permission.
 */

import { useState } from "react";

import { askForPermission } from "../../api/console-api";
import { useApi } from "../../api/context";
import { featureName, permissionName, t } from "../../i18n";
import { useAlerts } from "../../shell/alerts";
import { useStaff } from "../../shell/staff-context";
import { describeConfigFailure } from "../config-refusal";
import { WriteFailureNotice, useIntentWrite } from "../team/TeamDialogs";
import { askProblem, askRequest, askableBy } from "./ask-model";

const describeFailure = (error: unknown) => describeConfigFailure(error, {}, "features.stale");

export function AskForPermission() {
  const { client } = useApi();
  const { me } = useStaff();
  const [settingId, setSettingId] = useState("");
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const alerts = useAlerts();
  const save = useIntentWrite();

  const askable = askableBy(me);
  if (askable.length === 0) return null;

  const submit = () => {
    save.edited();
    void save.run(
      askProblem(settingId, until, reason),
      (idempotencyKey) => askForPermission(client, askRequest(settingId, until, reason), { idempotencyKey }),
      (detail) => {
        setSettingId("");
        setUntil("");
        setReason("");
        alerts.ok(t("access.ask.sent", { reference: detail.reference }));
      },
    );
  };

  return (
    <section className="card">
      <h2>{t("access.ask.title")}</h2>
      <p className="meta">{t("access.ask.explain")}</p>

      <form
        className="subject-bar"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="subject-uuid">
          <span className="subject-label">{t("access.ask.what")}</span>
          <select
            value={settingId}
            onChange={(event) => {
              save.edited();
              setSettingId(event.target.value);
            }}
          >
            <option value="">{t("access.ask.pickOne")}</option>
            {askable.map((each) => (
              <option key={each.settingId} value={each.settingId}>
                {permissionName(each.settingId)} — {featureName(each.feature as never)}
              </option>
            ))}
          </select>
        </label>
        <label className="subject-date">
          {/* Optional: an open-ended ask is a real one, and whoever decides can
              still grant it with an end date of their own. */}
          <span className="subject-label">{t("access.ask.until")}</span>
          <input type="date" value={until} onChange={(event) => setUntil(event.target.value)} />
        </label>
        <label className="subject-uuid">
          <span className="subject-label">{t("access.ask.why")}</span>
          <input
            type="text"
            value={reason}
            placeholder={t("access.ask.whyHint")}
            onChange={(event) => {
              save.edited();
              setReason(event.target.value);
            }}
          />
        </label>
        <button type="submit" className="btn primary" disabled={save.pending}>
          {t("access.ask.send")}
        </button>
        {save.problem ? <span className="subject-problem">{save.problem}</span> : null}
      </form>
      {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} /> : null}
    </section>
  );
}
